/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { Suspense, lazy } from "react";
import { Route, Routes } from "react-router";
import { IS_PROD } from "../utils/constants";
import { PageLoading } from "../ui/LoadingOverlay";
import Unsubscribe from "../pages/Unsubscribe";
import BookedIntakePreview from "../pages/dev/BookedIntakePreview";
import { Consult, Lyra, NewAccount, NewAccountAndConsult, PCScreener, Screener, ScreenerLayout } from "./screener";
import { ByAudience, PublicOnly, RequireAccount, useTrailingDotStrip } from "./gates";
import { auth, caregiver, provider, referral, status } from "./pages";

const Login = auth("Login");
const Logout = auth("Logout");
const TwoFactorPhone = auth("TwoFactorPhone");
const TwoFactorCode = auth("TwoFactorCode");
const Register = auth("Register");
const ForgotPassword = auth("ForgotPassword");
const PasswordReset = auth("PasswordReset");
const NotFound = status("NotFound");

const CaregiverHome = caregiver("CaregiverHome");
const ScheduleAppointment = caregiver("ScheduleAppointment");
const ScheduleIntake = caregiver("ScheduleIntake");
const ScheduleConsult = caregiver("ScheduleConsult");
const InsuranceCard = caregiver("InsuranceCard");
const Conversations = caregiver("Conversations");
const ProviderSelection = caregiver("ProviderSelection");
const Appointments = caregiver("Appointments");
/** Shared by the parent, child and public forms routes; its own chunk so the Healthie stylesheet is not hoisted into the caregiver one. */
const Forms = lazy(() => import("../pages/forms/Forms"));

const Directory = provider("Directory");
const Patient = provider("Patient");
const Invite = provider("Invite");
const InviteDetails = provider("InviteDetails");
const PendingInvites = provider("PendingInvites");
const Connection = provider("Connection");
const AddConnection = provider("AddConnection");
const RemoveConnection = provider("RemoveConnection");
const MyInbox = provider("MyInbox");
const Referrals = lazy(() => import("../pages/Referrals"));
const ReferringProviders = lazy(() => import("../pages/ExternalProviders"));
const ReferringOrganizations = lazy(() => import("../pages/ExternalOrganizations"));
const OrganizationMembers = lazy(() => import("../pages/OrganizationMembers"));

const ReferralLayout = referral("ReferralLayout");
const DashboardLayout = referral("DashboardLayout");
const PediatricianReferral = referral("PediatricianReferral");
const PatientsView = referral("PatientsView");
const ExternalOrganizationDashboard = referral("ExternalOrganizationDashboard");
const OneTimeTokenAuth = referral("OneTimeTokenAuth");

/** The whole tree, exported so a test can compare its paths with `ROUTE_TABLE`. */
export const routeElements = (
  <>
    <Route element={<PublicOnly />}>
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
    </Route>
    <Route path="/logout" element={<Logout />} />
    <Route path="/twoFactorAuthPhone" element={<TwoFactorPhone />} />
    <Route path="/twoFactorAuthSubmitCode" element={<TwoFactorCode />} />
    <Route path="/register" element={<Register />} />
    <Route path="/forgotPassword" element={<ForgotPassword />} />
    <Route path="/passwordReset" element={<PasswordReset />} />
    <Route path="/unsubscribe" element={<Unsubscribe />} />

    <Route element={<RequireAccount audiences={["parent"]} />}>
      <Route path="/home" element={<CaregiverHome />} />
      <Route path="/schedule-appointment" element={<ScheduleAppointment />} />
      <Route path="/schedule-intake" element={<ScheduleIntake />} />
      <Route path="/schedule-consult" element={<ScheduleConsult />} />
      <Route path="/add-insurance-card" element={<InsuranceCard />} />
      <Route path="/conversations" element={<Conversations />} />
      <Route path="/provider-selection" element={<ProviderSelection />} />
      <Route path="/appointments" element={<Appointments />} />
    </Route>
    <Route element={<RequireAccount audiences={["parent", "child"]} />}>
      <Route path="/complete-forms" element={<Forms />} />
    </Route>
    <Route element={<RequireAccount audiences={["parent", "careTeam"]} />}>
      <Route
        path="/conversations/:conversationId"
        element={<ByAudience parent={<Conversations />} careTeam={<MyInbox />} />}
      />
    </Route>
    <Route element={<RequireAccount audiences={["careTeam"]} />}>
      <Route path="/patients" element={<Directory />} />
      <Route path="/patient" element={<Patient />} />
      <Route path="/patient/:patientId" element={<Patient />} />
      <Route path="/patient/:patientId/caregivers/:caregiverId/conversations/:conversationId" element={<Patient />} />
      <Route path="/patient/:patientId/conversations/:conversationId" element={<Patient />} />
      <Route path="/patient/:patientId/assessment" element={<Patient />} />
      <Route path="/invite" element={<Invite />} />
      <Route path="/inviteDetails" element={<InviteDetails />} />
      <Route path="/pendingInvites" element={<PendingInvites />} />
      <Route path="/connection" element={<Connection />} />
      <Route path="/addConnection" element={<AddConnection />} />
      <Route path="/removeConnection" element={<RemoveConnection />} />
      <Route path="/myInbox" element={<MyInbox />} />
      <Route path="/referrals" element={<Referrals />} />
      <Route path="/referring-providers" element={<ReferringProviders />} />
      <Route path="/referring-organizations" element={<ReferringOrganizations />} />
      <Route path="/organization-members" element={<OrganizationMembers />} />
    </Route>

    <Route element={<ReferralLayout />}>
      <Route path="/refer" element={<PediatricianReferral />} />
      <Route path="/referral" element={<PediatricianReferral />} />
      <Route
        path="/view-referrals"
        element={
          <DashboardLayout>
            <PatientsView />
          </DashboardLayout>
        }
      />
      <Route
        path="/view-providers"
        element={
          <DashboardLayout>
            <ExternalOrganizationDashboard />
          </DashboardLayout>
        }
      />
      <Route path="/auth" element={<OneTimeTokenAuth />} />
    </Route>

    <Route element={<ScreenerLayout />}>
      <Route path="/consult" element={<Consult />} />
      <Route path="/pcscreener" element={<PCScreener />} />
      <Route path="/screener" element={<Screener />} />
      <Route path="/lyra" element={<Lyra />} />
      <Route path="/newaccount" element={<NewAccount />} />
      <Route path="/newaccountandconsult" element={<NewAccountAndConsult />} />
      <Route path="/forms" element={<Forms />} />
    </Route>

    {!IS_PROD && <Route path="/dev/booked-intake-preview" element={<BookedIntakePreview />} />}
    <Route path="*" element={<NotFound />} />
  </>
);

export default function AppRoutes() {
  useTrailingDotStrip();
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>{routeElements}</Routes>
    </Suspense>
  );
}
